import React, { useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, Check, Hourglass, LockKeyhole, Sparkles, KeyRound, Loader2 } from 'lucide-react';
import { KitModal } from '../da/Kit';
import { usePlus360, reloadPlus360 } from '../../hooks/usePlus360';
import { getApiUrl } from '../../utils/api';
import { Plus360Cta, Plus360Mark } from './Plus360';
import { Plus360Demo } from './demo/Plus360Demo';

const stateOf = (m, enabled) => {
  if (m.coming_soon) return { kicker: 'Bientôt disponible', tag: 'is-gris', Icon: Hourglass, text: 'Bientôt' };
  if (enabled) return { kicker: 'Inclus dans votre 360+', tag: 'is-mint', Icon: Check, text: 'Inclus' };
  return { kicker: 'Module verrouillé', tag: 'is-gris', Icon: LockKeyhole, text: 'Verrouillé' };
};

/** Saisie du code d'accès propriétaire pour déverrouiller le pack 360+. */
const Plus360CodeUnlock = ({ onUnlocked, testId }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${getApiUrl()}/plus-360/unlock`, { code: code.trim() }, { withCredentials: true });
      toast.success('Modules 360+ déverrouillés');
      reloadPlus360();
      onUnlocked?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Code invalide');
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button type="button" className="plus-code-trigger" onClick={() => setOpen(true)} data-testid={`${testId}-open`}>
        <KeyRound size={13} /> J'ai un code d'accès
      </button>
    );
  }
  return (
    <form className="plus-code-form" onSubmit={submit} data-testid={`${testId}-form`}>
      <input autoFocus type="password" value={code} onChange={(e) => setCode(e.target.value)}
        placeholder="Code d'accès" className="plus-code-input" data-testid={`${testId}-input`} />
      <button type="submit" className="fh-btn" disabled={busy} data-testid={`${testId}-submit`}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Déverrouiller
      </button>
    </form>
  );
};

/** Fiche vendeuse d'un module 360+ : visuel, accroche, ce que ça fait, bénéfices, prérequis, expert, prix + CTA. */
export const Plus360ModuleModal = ({ moduleKey, onClose, testId = 'plus360-module-modal' }) => {
  const { plus } = usePlus360();
  const { tenantSlug } = useParams();
  const mod = plus?.modules?.find((m) => m.key === moduleKey);
  if (!moduleKey) return null;
  const enabled = !!plus?.enabled;
  const st = stateOf(mod || {}, enabled);
  const price = plus?.price_eur_month ? Math.round(plus.price_eur_month) : 349;
  return <KitModal open={!!moduleKey} onClose={onClose} kicker={st.kicker} title={mod?.label || '360+'} testId={testId} wide>
    {mod && <div className="plus-fiche" data-testid={`${testId}-${mod.key}`}>
      <Plus360Demo mod={mod} testId={`${testId}-demo`}>
        <span className={`kit-tag ${st.tag} plus-fiche-state`} data-testid={`${testId}-state`}><st.Icon size={12} /> {st.text}</span>
      </Plus360Demo>
      <div className="plus-fiche-copy">
        <p className="plus-fiche-tagline" data-testid={`${testId}-tagline`}>{mod.tagline || mod.hook}</p>
        <p className="plus-fiche-desc" data-testid={`${testId}-description`}>{mod.description}</p>
        <div className="plus-fiche-block">
          <span className="kit-kicker">Ce que vous obtenez</span>
          <ul className="plus-fiche-features" data-testid={`${testId}-features`}>
            {(mod.features || []).map((f, i) => <li key={i}><span className="plus-fiche-check"><Check size={12} /></span>{f}</li>)}
          </ul>
        </div>
        {mod.requirements && <div className="plus-fiche-block">
          <span className="kit-kicker">Pour bien démarrer</span>
          <p className="plus-fiche-req" data-testid={`${testId}-requirements`}>{mod.requirements}</p>
        </div>}
        {mod.expert && <p className="plus-fiche-expert" data-testid={`${testId}-expert`}><Sparkles size={13} /> {mod.expert} suit ce module pour vous</p>}
      </div>
    </div>}
    <footer className="plus-fiche-foot hp-card" data-testid={`${testId}-foot`}>
      <div className="plus-fiche-foot-copy">
        <Plus360Mark size={22} testId={`${testId}-mark`} />
        <div>
          <strong data-testid={`${testId}-price`}>{enabled ? '360+ est actif dans votre établissement' : `${price} € HT / mois · les 7 modules inclus`}</strong>
          <span>{enabled ? 'Tous les modules experts sont ouverts.' : 'Par établissement · résiliable chaque mois · rien ne s’active sans votre confirmation.'}</span>
        </div>
      </div>
      {enabled && mod && !mod.coming_soon
        ? <Link to={`/${tenantSlug}/${mod.destination}`} className="fh-btn" onClick={onClose} data-testid={`${testId}-open`}>Ouvrir {mod.label}<ArrowUpRight size={16} /></Link>
        : enabled ? <span className="kit-tag is-gris" data-testid={`${testId}-soon`}><Hourglass size={12} /> Bientôt, inclus dans votre 360+</span>
        : <div className="plus-fiche-foot-actions"><Plus360Cta testId={`${testId}-cta`} /><Plus360CodeUnlock onUnlocked={onClose} testId={`${testId}-code`} /></div>}
    </footer>
  </KitModal>;
};

/** Fenêtre au clic sur un module verrouillé dans le menu. */
export const Plus360LockModal = ({ moduleKey, onClose }) => <Plus360ModuleModal moduleKey={moduleKey} onClose={onClose} testId="plus360-lock-modal" />;
